// Pure rules for the identity card (Prism spec kg_quote_path §8, rulings 8, 9).
//
// A verdict whose product differs from the worker's brand/model is ALSO an
// edit and a reprice (ruling 9): the page must say so before applying it.
// The worker keeps authority through the shadow, so the card never writes
// brand/model on its own; it hands the edit to today's patchItem path.

// Letters and digits only: "DS-1", "DS1" and "ds 1" are the same identity, so a
// dropped dash never counts as a change (it forced a reprice before 2026-09-07).
const norm = s => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

// True when recording `product` as the item's identity would change what the
// worker wrote (brand + model) — the case that needs the operator's choice
// between recording the verdict alone and also editing the item (a reprice).
export function verdictChangesWorkerIdentity(card, product) {
    if (!product?.display_name) return false;
    const worker = norm(`${card?.worker?.brand ?? ''} ${card?.worker?.model ?? ''}`);
    if (!worker) return true; // the worker wrote nothing: any product is a change
    return worker !== norm(product.display_name);
}

// The brand/model pair to PATCH for a product: the graph's brand string, and
// the display name minus that brand as the model text.
export function splitDisplayName(displayName, brand) {
    const name = String(displayName ?? '').trim();
    const b = String(brand ?? '').trim();
    if (b && name.toLowerCase().startsWith(b.toLowerCase() + ' ')) {
        return { brand: b, model: name.slice(b.length + 1).trim() };
    }
    if (b && name.toLowerCase() === b.toLowerCase()) return { brand: b, model: '' };
    return { brand: b || null, model: name };
}

// What the card shows as its headline state. `lookup_queued` (set by prism-api
// from the pricing worker's lookup queue) means the photo lookup has not run
// yet, often held by its daily cap: "No KG answer" would claim the KG gave up.
export function cardState(card) {
    if (!card) return 'no_answer';
    if (card.verdict) return 'verdict';
    if (card.pending) return 'identifying';
    const status = card.kg?.status ?? 'none';
    if (card.lookup_queued && (status === 'none' || status === 'candidate')) return 'lookup_queued';
    if (status === 'none') return 'no_answer';
    return status; // resolved | candidate | provisional
}

export const STATE_LABEL = {
    verdict: 'Verdict recorded',
    identifying: 'Identifying…',
    lookup_queued: 'Photo lookup queued',
    no_answer: 'No KG answer',
    resolved: 'KG answer',
    candidate: 'KG candidates only',
    provisional: 'Provisional',
};

export const AGREEMENT_LABEL = { agree: 'Agrees with worker', disagree: 'Disagrees with worker', unknown: 'Not comparable' };

// Is this item's identity confirmed (Zsolt, 2026-10-01)? Yes when the operator
// recorded a confirm or correct verdict, or when the KG answer is T0 (seen before)
// or T3 (web lookup) above 93% and agrees with what the worker wrote. A reject
// verdict is never a confirmation. The consignment gate uses this in place of the
// engine's identity score, which reads low on items whose model is plain to see.
// The API sends the tier as text ('0', '3'); compare as text so a number works too.
const CONFIRMING_TIERS = new Set(['0', '3']);
const MIN_CONFIRMING_CONFIDENCE = 0.93;

export function kgConfirmsIdentity(card) {
    if (!card) return false;
    if (card.verdict) return card.verdict.kind === 'confirm' || card.verdict.kind === 'correct';
    const kg = card.kg;
    return kg?.status === 'resolved'
        && CONFIRMING_TIERS.has(String(kg.tier))
        && typeof kg.confidence === 'number' && kg.confidence > MIN_CONFIRMING_CONFIDENCE
        && card.agreement === 'agree';
}

// Adds `identity_confirmed` to each row's engine signals, from that item's own
// card (by quote_item_id). A row with no market read stays null.
export function withIdentityConfirmation(rowSignals, items, cardsById) {
    return rowSignals.map((signals, i) => signals && {
        ...signals,
        identity_confirmed: kgConfirmsIdentity(cardsById?.[items[i]?.quote_item_id]),
    });
}

// The verdict request body for the Prism endpoint (VerdictRequest contract).
export function toVerdictBody({ kind, product, newProduct, rejectedProductId }) {
    if (kind === 'confirm') return { kind, product_model_id: product.product_model_id };
    if (kind === 'correct' && newProduct) return { kind, new_product: { brand_name: newProduct.brand_name, model: newProduct.model } };
    if (kind === 'correct') return { kind, product_model_id: product.product_model_id };
    return { kind: 'reject', rejected_product_model_id: rejectedProductId };
}
